/**
 * Subtle ambient drift of the rink, so it feels like the play area is
 * floating in space. Applied as a CSS transform on the canvas element so
 * the projection math, pointer mapping, and HUD overlay all stay
 * consistent: getBoundingClientRect() reflects the transform, so
 * screenToRink continues to map cursor → rink coords correctly.
 *
 * Composes additively with screen shake (which is applied INSIDE the
 * canvas) — shake is pixel-level jitter on game-world contents, hover is
 * the canvas element drifting in the DOM.
 */

const AMBIENT_AMP_X = 18;     // px
const AMBIENT_AMP_Y = 10;
const AMBIENT_PERIOD_X_MS = 17000;
const AMBIENT_PERIOD_Y_MS = 13000;

const BOB_DURATION_MS = 700;
const BOB_AMP_PX = 14;        // downward dip in screen px

let lastBobAt = -Infinity;

export function triggerGoalBob(now: number): void {
  lastBobAt = now;
}

export function getHoverOffset(now: number): { dx: number; dy: number } {
  const dxAmbient =
    AMBIENT_AMP_X * Math.sin((now / AMBIENT_PERIOD_X_MS) * Math.PI * 2);
  const dyAmbient =
    AMBIENT_AMP_Y * Math.sin((now / AMBIENT_PERIOD_Y_MS) * Math.PI * 2);

  let dyBob = 0;
  const age = now - lastBobAt;
  if (age >= 0 && age < BOB_DURATION_MS) {
    const t = age / BOB_DURATION_MS;
    // Starts at 0, peaks around t=0.3, returns to 0 at t=1.
    dyBob = BOB_AMP_PX * Math.sin(t * Math.PI) * (1 - t);
  }

  return { dx: dxAmbient, dy: dyAmbient + dyBob };
}

/**
 * Drive the canvas's CSS transform from the current hover offset. Call
 * once per render frame. `transform` is a composited property, so this
 * doesn't trigger layout.
 */
export function applyHoverTransform(canvas: HTMLCanvasElement, now: number): void {
  const o = getHoverOffset(now);
  canvas.style.transform = `translate(${o.dx.toFixed(2)}px, ${o.dy.toFixed(2)}px)`;
}
